import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

const UserApproval = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pending, all] = await Promise.all([
        adminAPI.getPendingUsers(),
        adminAPI.getUsers(),
      ]);
      setPendingUsers(pending.data || []);
      setAllUsers(all.data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await adminAPI.approveUser(userId);
      loadData();
    } catch (err) {
      alert('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (window.confirm('Are you sure you want to reject this user registration?')) {
      try {
        await adminAPI.rejectUser(userId);
        loadData();
      } catch (err) {
        alert('Failed to reject user');
      }
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading users...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">User Management</h2>
        <p className="page-subtitle">Approve or reject user registration requests</p>
      </div>

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            ...styles.tab,
            ...(activeTab === 'pending' ? styles.activeTab : {}),
          }}
        >
          Pending Approvals ({pendingUsers.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            ...styles.tab,
            ...(activeTab === 'all' ? styles.activeTab : {}),
          }}
        >
          All Users ({allUsers.length})
        </button>
      </div>

      {activeTab === 'pending' && (
        <div style={styles.section}>
          <h3>Pending User Registrations</h3>
          {pendingUsers.length === 0 ? (
            <div className="card" style={styles.empty}>No pending user registrations</div>
          ) : (
            <div style={styles.userList}>
              {pendingUsers.map((user) => (
                <div key={user.id} className="card">
                  <div style={styles.userInfo}>
                    <strong>{user.username}</strong>
                    <span>{user.email}</span>
                    <span style={styles.date}>
                      Registered: {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={styles.actions}>
                    <button
                      onClick={() => handleApprove(user.id)}
                      className="btn btn-success"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(user.id)}
                      className="btn btn-danger"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'all' && (
        <div style={styles.section}>
          <h3>All Users</h3>
          <div style={styles.userList}>
          {allUsers.map((user) => (
            <div key={user.id} className="card">
              <div style={styles.userInfo}>
                <strong>{user.username}</strong>
                <span>{user.email}</span>
                <div style={styles.badges}>
                  {user.is_admin === 1 && (
                    <span className="badge badge-warning">Admin</span>
                  )}
                  {user.is_approved === 1 ? (
                    <span className="badge badge-success">Approved</span>
                  ) : (
                    <span className="badge badge-info">Pending</span>
                  )}
                </div>
                <span style={styles.date}>
                  Joined: {new Date(user.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  tabs: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    borderBottom: '2px solid #e5e7eb',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '0.9375rem',
    color: '#64748b',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    color: '#2563eb',
    borderBottom: '2px solid #2563eb',
  },
  section: {
    marginTop: '2rem',
  },
  userList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  userCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    flex: 1,
  },
  badges: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.25rem',
  },
  date: {
    fontSize: '0.875rem',
    color: '#64748b',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem',
    color: '#64748b',
  },
  loading: {
    textAlign: 'center',
    padding: '4rem 2rem',
    color: '#64748b',
  },
};

export default UserApproval;
